// Generated by Claude Code
import { createContext, createElement, forwardRef, useContext } from 'react'
import type { LucideProps } from 'lucide-react'
import { cn } from '#core/utils/cn.ts'

export type IconVariant = 'line' | 'fill'

export interface IconProps extends LucideProps {
  variant?: IconVariant
}

/** Sets the variant of every icon below it that has no `variant` prop of its own. */
export const IconVariantContext = createContext<IconVariant | undefined>(undefined)

const variantIcons = new WeakSet<object>()

/** Whether the icon draws both a line and a fill version. */
export const hasVariants = (icon: object) => variantIcons.has(icon)

const pascal = (name: string) => name.replace(/(^|-)(\w)/g, (_, __, c: string) => c.toUpperCase())

/**
 * Builds a lucide drop-in icon that draws its line or fill version of a Coolicons glyph. Without
 * `fill` paths it is line-only, for a glyph whose filled shape would mean something else. The line
 * version's stroke is in screen pixels, so it keeps one weight at every size.
 */
export function createIcon(
  name: string,
  paths: { line: string[]; fill?: string[] },
  defaultVariant: IconVariant = 'line',
) {
  const solidByName = name.endsWith('-filled')
  const Icon = forwardRef<SVGSVGElement, IconProps>(
    (
      {
        size = 24,
        color = 'currentColor',
        strokeWidth = 1.5,
        absoluteStrokeWidth: _absoluteStrokeWidth,
        variant,
        className,
        children,
        ...rest
      },
      ref,
    ) => {
      const inherited = useContext(IconVariantContext)
      const resolved = paths.fill ? (variant ?? (solidByName ? 'fill' : inherited) ?? defaultVariant) : 'line'
      const line = resolved === 'line'
      return createElement(
        'svg',
        {
          ref,
          xmlns: 'http://www.w3.org/2000/svg',
          width: size,
          height: size,
          viewBox: '0 0 24 24',
          fill: line ? 'none' : color,
          stroke: line ? color : 'none',
          ...(line && {
            strokeWidth,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }),
          className: cn('lucide', `lucide-${name}`, className),
          ...rest,
        },
        ...(paths[resolved] ?? paths.line).map((d, i) =>
          createElement('path', { key: i, d, vectorEffect: line ? 'non-scaling-stroke' : undefined }),
        ),
        children,
      )
    },
  )
  Icon.displayName = pascal(name)
  if (paths.fill) variantIcons.add(Icon)
  return Icon
}
